import { ModelBase } from '../abstract/model-base.model';
import { LegalStatusEnum } from '../../enums/legal-status.enum';
import { MfaPolicy } from '../authorization/mfa-policy.model';
import { Address } from '../address/address.model';
import { AiSettings } from '../ai/ai-settings.model';
import { ClientContact } from './contact.model';
import { Currency } from '../core/currency.model';
import { Country } from '../core/country.model';
import { Timezone } from '../core/timezone.model';
import { TaxationSettings } from '../taxation/taxation-settings.model';
import { AccountingSettings } from '../accounting/accounting-settings.model';
import { SalesSettings } from '../sales/sales-settings.model';
import { AccountingPeriod } from '../accounting/accounting-period.model';
import { OrganizationActivity } from '../legal-entity/organization-activity.model';
import { Organization } from '../legal-entity/organization.model';
import { Subscription } from '../subscription/subscription.model';
import { OrganizationSize } from '../legal-entity/organization-size.model';
import { BankingSettings } from '../banking/banking-settings.model';

export class Client extends ModelBase {

    static override CLASS_NAME: string = 'Client';

    private _address!: Address;
    private _businessContext!: string;
    private _commercialName!: string;
    private _contacts!: ClientContact[];
    private _country!: Country;
    private _currency!: Currency;
    private _biifor!: boolean;
    private _demo!: boolean;
    private _identifier!: string;
    private _logo!: string;
    private _avatar!: string;
    private _mainActivity!: OrganizationActivity;
    private _name!: string;
    private _organization!: Organization;
    private _owner!: ClientContact;
    private _shareCapital!: number;
    private _size!: OrganizationSize;
    private _subscription!: Subscription;
    private _timezone!: Timezone;
    // Gestion cabinet comptable
    private _accountant!: boolean;
    private _accountingFirm!: Client;
    // Paramétrages
    private _accountingPeriods: AccountingPeriod[] = [];
    private _accountingSettings!: AccountingSettings;
    private _aiSettings!: AiSettings;
    private _bankingSettings!: BankingSettings;
    private _salesSettings!: SalesSettings;
    private _taxationSettings!: TaxationSettings;
    // Sécurité
    private _mfaPolicy!: MfaPolicy;
    // Consentements
    private _cgvStatus!: LegalStatusEnum;
    // Compteurs
    private _draftPurchaseInvoicesCount!: number;
    private _draftSalesInvoicesCount!: number;
    private _unprocessedBankAccountLinesCount!: number;
    private _validatedPurchaseInvoicesCount!: number;
    private _partiallyPaidPurchaseInvoicesCount!: number;
    private _validatedSalesInvoicesCount!: number;
    private _partiallyPaidSalesInvoicesCount!: number;
    private _unjustifiedBankAccountLinesCount!: number;
    private _partiallyJustifiedBankAccountLinesCount!: number;
    // Autres
    public deletePhrase: string | null = null;
    public resetPhrase: string | null = null;

    static override init(): any {
        let instance = super.init();
        instance.accountingPeriods = [];
        instance.accountingSettings = AccountingSettings.init();
        instance.aiSettings = AiSettings.init();
        instance.salesSettings = SalesSettings.init();
        instance.taxationSettings = TaxationSettings.init();
        return instance;
    }

    public hasFeature(feature: string): boolean {
        return this.subscription.hasFeature(feature);
    }

    public get isCaledonian(): boolean {
        return this._country.alpha2 === 'NC';
    }

    public get isPolynesian(): boolean {
        return this._country?.alpha2 === 'PF';
    }

    public get accountant(): boolean {
        return this._accountant;
    }

    public set accountant(value: any) {
        this._setValue('_accountant', value, 'boolean');
    }

    public get accountingFirm(): Client {
        return this._accountingFirm;
    }

    public set accountingFirm(value: any) {
        this._setValue('_accountingFirm', value, 'Client');
    }

    public get accountingPeriods(): AccountingPeriod[] {
        return this._accountingPeriods;
    }

    public set accountingPeriods(value: any) {
        this._setValue('_accountingPeriods', value, 'AccountingPeriod');
    }

    public get accountingSettings(): AccountingSettings {
        return this._accountingSettings;
    }

    public set accountingSettings(value: any) {
        this._setValue('_accountingSettings', value, 'AccountingSettings');
    }

    public get address(): Address {
        return this._address;
    }

    public set address(value: any) {
        this._setValue('_address', value, 'Address');
    }

    public get aiSettings(): AiSettings {
        return this._aiSettings;
    }

    public set aiSettings(value: any) {
        this._setValue('_aiSettings', value, 'AiSettings');
    }

    public get bankingSettings(): BankingSettings {
        return this._bankingSettings;
    }

    public set bankingSettings(value: any) {
        this._setValue('_bankingSettings', value, 'BankingSettings');
    }

    public get biifor(): boolean {
        return this._biifor;
    }

    public set biifor(value: any) {
        this._setValue('_biifor', value, 'boolean');
    }

    public get businessContext(): string {
        return this._businessContext;
    }

    public set businessContext(value: any) {
        this._setValue('_businessContext', value, 'string');
    }

    public get cgvStatus(): LegalStatusEnum {
        return this._cgvStatus;
    }

    public set cgvStatus(value: any) {
        this._setValue('_cgvStatus', value, 'string');
    }

    public get mfaPolicy(): MfaPolicy {
        return this._mfaPolicy;
    }

    public set mfaPolicy(value: any) {
        this._setValue('_mfaPolicy', value, 'MfaPolicy');
    }

    public get commercialName(): string {
        return this._commercialName;
    }

    public set commercialName(value: any) {
        this._setValue('_commercialName', value, 'string');
    }

    public get contacts(): ClientContact[] {
        return this._contacts;
    }

    public set contacts(value: any) {
        this._setValue('_contacts', value, 'PartnerContact');
    }

    public get country(): Country {
        return this._country;
    }

    public set country(value: any) {
        this._setValue('_country', value, 'Country');
    }

    public get currency(): Currency {
        return this._currency;
    }

    public set currency(value: any) {
        this._setValue('_currency', value, 'Currency');
    }

    public get demo(): boolean {
        return this._demo;
    }

    public set demo(value: any) {
        this._setValue('_demo', value, 'boolean');
    }

    public get draftPurchaseInvoicesCount(): number {
        return this._draftPurchaseInvoicesCount;
    }

    public set draftPurchaseInvoicesCount(value: any) {
        this._setValue('_draftPurchaseInvoicesCount', value, 'integer');
    }

    public get draftSalesInvoicesCount(): number {
        return this._draftSalesInvoicesCount;
    }

    public set draftSalesInvoicesCount(value: any) {
        this._setValue('_draftSalesInvoicesCount', value, 'integer');
    }

    public get identifier(): string {
        return this._identifier;
    }

    public set identifier(value: any) {
        this._setValue('_identifier', value, 'string');
    }

    public get logo(): string {
        return this._logo;
    }

    public set logo(value: any) {
        this._setValue('_logo', value, 'string');
    }

    public get avatar(): string {
        return this._avatar;
    }

    public set avatar(value: any) {
        this._setValue('_avatar', value, 'string');
    }

    public get mainActivity(): OrganizationActivity {
        return this._mainActivity;
    }

    public set mainActivity(value: any) {
        this._setValue('_mainActivity', value, 'OrganizationActivity');
    }

    public get name(): string {
        return this._name;
    }

    public set name(value: any) {
        this._setValue('_name', value, 'string');
    }

    public get organization(): Organization {
        return this._organization;
    }

    public set organization(value: any) {
        this._setValue('_organization', value, 'Organization');
    }

    public get owner(): ClientContact {
        return this._owner;
    }

    public set owner(value: any) {
        this._setValue('_owner', value, 'ClientContact');
    }

    public get partiallyJustifiedBankAccountLinesCount(): number {
        return this._partiallyJustifiedBankAccountLinesCount;
    }

    public set partiallyJustifiedBankAccountLinesCount(value: any) {
        this._setValue('_partiallyJustifiedBankAccountLinesCount', value, 'integer');
    }

    public get partiallyPaidPurchaseInvoicesCount(): number {
        return this._partiallyPaidPurchaseInvoicesCount;
    }

    public set partiallyPaidPurchaseInvoicesCount(value: any) {
        this._setValue('_partiallyPaidPurchaseInvoicesCount', value, 'integer');
    }

    public get partiallyPaidSalesInvoicesCount(): number {
        return this._partiallyPaidSalesInvoicesCount;
    }

    public set partiallyPaidSalesInvoicesCount(value: any) {
        this._setValue('_partiallyPaidSalesInvoicesCount', value, 'integer');
    }

    public get salesSettings(): SalesSettings {
        return this._salesSettings;
    }

    public set salesSettings(value: any) {
        this._setValue('_salesSettings', value, 'SalesSettings');
    }

    public get shareCapital(): number {
        return this._shareCapital;
    }

    public set shareCapital(value: any) {
        this._setValue('_shareCapital', value, 'float');
    }

    public get size(): OrganizationSize {
        return this._size;
    }

    public set size(value: any) {
        this._setValue('_size', value, 'OrganizationSize');
    }

    public get subscription(): Subscription {
        return this._subscription;
    }

    public set subscription(value: any) {
        this._setValue('_subscription', value, 'Subscription');
    }

    public get taxationSettings(): TaxationSettings {
        return this._taxationSettings;
    }

    public set taxationSettings(value: any) {
        this._setValue('_taxationSettings', value, 'TaxationSettings');
    }

    public get timezone(): Timezone {
        return this._timezone;
    }

    public set timezone(value: any) {
        this._setValue('_timezone', value, 'Timezone');
    }

    public get unjustifiedBankAccountLinesCount(): number {
        return this._unjustifiedBankAccountLinesCount;
    }

    public set unjustifiedBankAccountLinesCount(value: any) {
        this._setValue('_unjustifiedBankAccountLinesCount', value, 'integer');
    }

    public get unprocessedBankAccountLinesCount(): number {
        return this._unprocessedBankAccountLinesCount;
    }

    public set unprocessedBankAccountLinesCount(value: any) {
        this._setValue('_unprocessedBankAccountLinesCount', value, 'integer');
    }

    public get validatedPurchaseInvoicesCount(): number {
        return this._validatedPurchaseInvoicesCount;
    }

    public set validatedPurchaseInvoicesCount(value: any) {
        this._setValue('_validatedPurchaseInvoicesCount', value, 'integer');
    }

    public get validatedSalesInvoicesCount(): number {
        return this._validatedSalesInvoicesCount;
    }

    public set validatedSalesInvoicesCount(value: any) {
        this._setValue('_validatedSalesInvoicesCount', value, 'integer');
    }
}
